import React from "react";
import { Chip } from "Components/Base/DataDisplay/Chip";
import { LockIcon, StarIcon } from "Components/Icons";

/**
 * Configuration object for different chip types used in learning activity cards
 * @type {Object.<string, {color: string, sx: Object, icon?: JSX.Element}>}
 */
const LEARNING_ACTIVITY_CHIPS = {
  mandatory: {
    color: "error",
    sx: { backgroundColor: "var(--colorErrorLight)" },
  },
  completed: {
    color: "success",
    sx: { backgroundColor: "var(--colorSuccessLight)" },
  },
  teams: {
    sx: {
      backgroundColor: "var(--colorInfoLight)",
      color: "var(--colorInfoDark)",
    },
  },
  requested: {
    color: "warning",
    sx: { backgroundColor: "var(--colorWarningLight)" },
  },
  attest: {
    sx: {
      backgroundColor: "var(--colorInfoLight)",
      color: "var(--colorInfoDark)",
    },
  },
  survey: {
    sx: {
      backgroundColor: "var(--colorInfoLight)",
      color: "var(--colorInfoDark)",
    },
  },
  locked: {
    color: "warning",
    icon: <LockIcon />,
    sx: { backgroundColor: "var(--colorWarningLight)" },
  },
  rating: {
    color: "success",
    icon: <StarIcon />,
    sx: { backgroundColor: "var(--colorSuccessLight)" },
  },
  administrator: {
    color: "success",
    sx: { backgroundColor: "var(--colorSuccessLight)" },
  },
  category: {
    sx: {
      backgroundColor: "var(--colorNeutralLight)",
      color: "var(--colorNeutralDark)",
    },
  },
};

/**
 * Renders a chip with the specified type and label
 * @param {Object} props - Component props
 * @param {string} props.type - Type of chip to render (must be a key in LEARNING_ACTIVITY_CHIPS)
 * @param {string} props.label - Text to display in the chip
 * @returns {JSX.Element} Rendered chip component
 */
const ChipRenderer = ({ type, label, ariaLabel }) => {
  const config = LEARNING_ACTIVITY_CHIPS[type];
  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      color={config.color || "default"}
      {...(config.sx && { sx: config.sx })}
      {...(config.icon && { icon: config.icon })}
      aria-label={ariaLabel}
    />
  );
};

export const AdministratorChip = ({ label }) => (
  <ChipRenderer type="administrator" label={label} />
);
export const MandatoryChip = ({ label }) => (
  <ChipRenderer type="mandatory" label={label} />
);
export const CompletedChip = ({ label }) => (
  <ChipRenderer type="completed" label={label} />
);
export const TeamsChip = ({ label }) => (
  <ChipRenderer type="teams" label={label} />
);
export const RequestedChip = ({ label }) => (
  <ChipRenderer type="requested" label={label} />
);
export const AttestChip = ({ label }) => (
  <ChipRenderer type="attest" label={label} />
);
export const SurveyChip = ({ label }) => (
  <ChipRenderer type="survey" label={label} />
);
export const LockedChip = ({ label }) => (
  <ChipRenderer type="locked" label={label} />
);
export const RatingChip = ({ label, ariaLabel }) => (
  <ChipRenderer type="rating" label={label} ariaLabel={ariaLabel} />
);
export const CategoryChip = ({ label, ariaLabel }) => (
  <ChipRenderer type="category" label={label} ariaLabel={ariaLabel} />
);
